/**
 * Design foundations — single source of truth for the design language.
 *
 * Three layers, each reading only from the one above it:
 *
 *   1. Primitives  raw values (hex, px). Never referenced from UI code.
 *   2. Semantic    what a value MEANS (text.secondary, border.subtle, state.selected).
 *   3. Ant tokens  theme/index.js + theme/components.js map semantics onto antd.
 *
 * Re-skin the product → change layer 1.
 * Change how a role looks everywhere (e.g. all secondary text) → change layer 2.
 * Never put a raw hex in a component override; add a semantic alias instead.
 */

export function alpha(hex, opacity) {
  const value = parseInt(hex.replace('#', ''), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/* ------------------------------------------------------------------ */
/* 1. Primitives                                                       */
/* ------------------------------------------------------------------ */

export const palette = {
  // Brand green, anchored on #067C60 at 500.
  brand: {
    50: '#ECF7F3',
    100: '#D3EDE4',
    200: '#A7DAC8',
    300: '#6FC0A5',
    400: '#1F9676', // hover
    500: '#067C60', // primary
    600: '#05684F', // pressed / selected text
    700: '#04543F',
    800: '#033F30',
    900: '#022B21',
  },

  // Neutral grays aligned with Ant Design chrome (#fafafa / rgba(0,0,0,0.45)).
  // Dark steps keep brand navy (#2E405E at 800) for primary text and sider.
  neutral: {
    0: '#FFFFFF',
    25: '#FAFAFA',
    50: '#FAFAFA',
    100: '#F0F0F0',
    200: '#E5E5E5',
    300: '#D4D4D4',
    400: '#A3A3A3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#2E405E',
    900: '#1B2740',
    950: '#111A2C',
  },

  // Status hues are deliberately distinct from brand green so "success"
  // never reads as "primary action", and "info" is no longer brand-colored.
  success: '#12B76A',
  warning: '#F79009',
  error: '#F04438',
  info: '#2E90FA',
};

export const typography = {
  fontFamily:
    "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontFamilyCode: "'JetBrains Mono', 'SFMono-Regular', Menlo, Consolas, monospace",
  // Dense product UI: 13px body on a 4px baseline (13 / 20 line height).
  size: {
    sm: 12,
    base: 13,
    lg: 15,
    xl: 18,
    h1: 30,
    h2: 24,
    h3: 20,
    h4: 16,
    h5: 14,
  },
  lineHeight: 20 / 13,
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
};

// Radius grows with the size of the surface: tags < controls < cards < dialogs.
export const radius = { xs: 4, sm: 6, md: 8, lg: 12, xl: 16, full: 999 };

export const size = {
  controlSM: 24,
  control: 32,
  controlLG: 40,
  header: 68,
};

// Elevation ladder. Each level has one job; don't borrow a level for decoration.
const shadowInk = palette.neutral[950];
export const shadow = {
  xs: `0 1px 2px ${alpha(shadowInk, 0.05)}`, // resting cards, default buttons
  sm: `0 1px 3px ${alpha(shadowInk, 0.08)}, 0 1px 2px ${alpha(shadowInk, 0.04)}`,
  md: `0 6px 16px -4px ${alpha(shadowInk, 0.1)}, 0 2px 4px -2px ${alpha(shadowInk, 0.05)}`, // dropdowns, hovered cards
  lg: `0 16px 32px -8px ${alpha(shadowInk, 0.14)}, 0 4px 8px -4px ${alpha(shadowInk, 0.06)}`, // popovers
  xl: `0 24px 56px -12px ${alpha(shadowInk, 0.22)}`, // modals, drawers
};

export const motion = {
  fast: '0.12s',
  mid: '0.2s',
  slow: '0.3s',
  easeOut: 'cubic-bezier(0.2, 0, 0, 1)',
};

/* ------------------------------------------------------------------ */
/* 2. Semantic tokens                                                  */
/* ------------------------------------------------------------------ */

const { brand, neutral } = palette;

export const semantic = {
  text: {
    heading: neutral[900],
    primary: neutral[800],
    secondary: neutral[500], // 4.8:1 on white, passes WCAG AA
    tertiary: neutral[400], // placeholders and disabled only
    inverse: neutral[0],
    link: brand[500],
    linkHover: brand[400],
  },
  surface: {
    canvas: neutral[50], // page background behind cards
    container: neutral[0], // cards, inputs, tables
    elevated: neutral[0], // dropdowns, popovers, modals
    subtle: neutral[50], // table header, input addons, card actions
    sider: neutral[900],
    spotlight: neutral[900], // tooltips
  },
  border: {
    strong: neutral[300], // control hover
    default: neutral[200], // form controls
    subtle: neutral[100], // cards, dividers, table rows
  },
  state: {
    hover: neutral[50],
    pressed: neutral[100],
    selected: brand[50],
    selectedHover: brand[100],
    focusRing: alpha(brand[500], 0.18),
  },
};
